import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { useQueryParams } from '../../hooks/useQueryParams';
import { dateTime } from '../../services/format';
import { Alert, Badge, Cell, Field, Filters, Input, PageTitle, Pager, Row, Select, Table, statusTone } from '../../components/ui';

const PAGE_SIZE = 50;
const TEMPLATES = ['verify_email', 'reset_password', 'invitation', 'welcome', 'invoice', 'staff_new_signup'];

export function MailList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  const status = get('status');
  const to = get('to');
  const template = get('template');
  const { data, error, loading } = useAsync(() => api.mail({ status: status || undefined, to: to || undefined, template: template || undefined, page, page_size: PAGE_SIZE }), [status, to, template, page]);

  return (
    <div>
      <PageTitle title={t('mail.title')} subtitle={data ? t('common.count', { count: data.total }) : undefined} />
      <Filters onReset={reset}>
        <Field label={t('mail.statusLabel')}>
          <Select value={status} onChange={(e) => set({ status: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {(['queued', 'sent', 'failed'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`mail.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('mail.to')} className="w-64">
          <Input value={to} onChange={(e) => set({ to: e.target.value })} />
        </Field>
        <Field label={t('mail.template')}>
          <Select value={template} onChange={(e) => set({ template: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {TEMPLATES.map((tpl) => (
              <option key={tpl} value={tpl}>
                {tpl}
              </option>
            ))}
          </Select>
        </Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      <Table head={['#', t('mail.to'), t('mail.template'), t('mail.subject'), t('mail.statusLabel'), t('mail.attempts'), t('mail.scheduledAt'), t('mail.sentAt')]} empty={loading ? t('common.loading') : t('common.empty')}>
        {(data?.items ?? []).map((m) => (
          <Row key={m.id} onClick={() => navigate(`/mail/${m.id}`)}>
            <Cell mono>{m.id}</Cell>
            <Cell mono>{m.to_email}</Cell>
            <Cell mono>{m.template}</Cell>
            <Cell className="max-w-xs truncate" title={m.subject}>{m.subject}</Cell>
            <Cell>
              <Badge tone={statusTone(m.status)}>{t(`mail.status.${m.status}`)}</Badge>
            </Cell>
            <Cell mono>{m.attempts}</Cell>
            <Cell mono>{dateTime(m.scheduled_at, i18n.language)}</Cell>
            <Cell mono>{dateTime(m.sent_at, i18n.language)}</Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
    </div>
  );
}
